import React, { useState, useEffect } from 'react';
import { FaTrash, FaSearch, FaEye, FaSortAmountDown, FaSortAmountUp, FaEdit, FaMapMarkerAlt, FaTimes, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { API_URL } from '../config';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

function ViewData() {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [categories, setCategories] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedPlace, setSelectedPlace] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [placeToDelete, setPlaceToDelete] = useState(null);
  const [sortOrder, setSortOrder] = useState('newest');
  const itemsPerPage = 9;
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [placesResponse, categoriesResponse] = await Promise.all([
          axios.get(`${API_URL}/places`),
          axios.get(`${API_URL}/categories`)
        ]);
        
        const processedPlaces = placesResponse.data.map(place => ({
          ...place,
          id: place.id,
          images: place.images ? (typeof place.images === 'string' 
            ? place.images.split(',').filter(img => img && img.trim() !== '') 
            : Array.isArray(place.images) 
              ? place.images.filter(img => img && img.trim() !== '') 
              : []) 
            : [],
          name: place.name || 'Untitled Place',
          description: place.description || 'No description available',
          category_name: place.category_name || 'Uncategorized',
          created_at: place.created_at || new Date().toISOString(),
          tags: Array.isArray(place.tags) ? place.tags : [],
          key_information: Array.isArray(place.key_information) ? place.key_information : []
        }));
        
        setPlaces(processedPlaces);
        setCategories(categoriesResponse.data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleDelete = async () => {
    if (!placeToDelete) return;
    try {
      await axios.delete(`${API_URL}/places/${placeToDelete.id}`);
      setPlaces(places.filter(place => place.id !== placeToDelete.id));
      setShowDeleteModal(false);
      setPlaceToDelete(null);
    } catch (err) {
      setError(err.message);
      setShowDeleteModal(false);
      setPlaceToDelete(null);
    }
  };

  const filteredPlaces = places.filter(place => {
    const matchesSearch = place.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         place.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === 'all' || place.category_name === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const sortedPlaces = [...filteredPlaces].sort((a, b) => {
    if (sortOrder === 'newest') {
      return new Date(b.created_at) - new Date(a.created_at);
    } else {
      return new Date(a.created_at) - new Date(b.created_at);
    }
  });

  const totalPages = Math.ceil(sortedPlaces.length / itemsPerPage);
  const currentPlaces = sortedPlaces.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-3 border-indigo-200 border-t-indigo-600 rounded-full animate-spin" />
          <p className="text-slate-500 font-medium">Loading data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-ruby-100 flex items-center justify-center">
            <FaTimes className="w-7 h-7 text-ruby-500" />
          </div>
          <p className="text-slate-900 font-medium mb-1">Error loading data</p>
          <p className="text-slate-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Browse All Places</h1>
          <p className="text-slate-500 mt-1">{sortedPlaces.length} destinations in the database</p>
        </div>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="input-group flex-1 sm:w-64">
            <span className="input-icon">
              <FaSearch className="w-4 h-4" />
            </span>
            <input
              type="text"
              placeholder="Search places..."
              value={searchTerm}
              onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="input input-with-icon"
            />
          </div>
          
          <select
            value={selectedCategory}
            onChange={(e) => { setSelectedCategory(e.target.value); setCurrentPage(1); }}
            className="input w-full sm:w-auto"
          >
            <option value="all">All Categories</option>
            {categories.map(category => (
              <option key={category.id} value={category.name}>
                {category.name}
              </option>
            ))}
          </select>
          
          <button
            onClick={() => setSortOrder(sortOrder === 'newest' ? 'oldest' : 'newest')}
            className="btn btn-secondary"
          >
            {sortOrder === 'newest' ? (
              <>
                <FaSortAmountDown className="w-4 h-4 mr-2" />
                Newest
              </>
            ) : (
              <>
                <FaSortAmountUp className="w-4 h-4 mr-2" />
                Oldest
              </>
            )}
          </button>
        </div>
      </div>

      {/* Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {currentPlaces.map((place, index) => (
          <div 
            key={place.id}
            className="card card-hover overflow-hidden animate-fade-in-up"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            {/* Image */}
            <div className="relative h-48 overflow-hidden">
              {place.images && place.images.length > 0 ? (
                <>
                  <img
                    src={place.images[0]}
                    alt={place.name}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900/60 to-transparent" />
                </>
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-indigo-100 to-indigo-200 flex items-center justify-center">
                  <FaMapMarkerAlt className="w-10 h-10 text-indigo-300" />
                </div>
              )}
              
              {/* Category Badge */}
              <div className="absolute top-4 left-4">
                <span className="badge bg-white/90 backdrop-blur-sm text-slate-700 shadow-lg">
                  {place.category_name}
                </span>
              </div>
              
              {/* Title Overlay */}
              <div className="absolute bottom-4 left-4 right-4">
                <h2 className="text-xl font-display font-bold text-white truncate">{place.name}</h2>
                <p className="text-white/80 text-sm flex items-center gap-1 mt-1">
                  <FaMapMarkerAlt className="w-3 h-3" />
                  {place.city}, {place.state}
                </p>
              </div>
            </div>
            
            {/* Content */}
            <div className="p-5">
              <p className="text-slate-600 text-sm line-clamp-2 mb-4">{place.description}</p>
              
              {/* Tags */}
              {place.tags && place.tags.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-4">
                  {place.tags.slice(0, 3).map((tag, idx) => (
                    <span key={idx} className="badge badge-indigo text-xs">
                      {tag}
                    </span>
                  ))}
                  {place.tags.length > 3 && (
                    <span className="badge bg-slate-100 text-slate-500 text-xs">
                      +{place.tags.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* Footer */}
              <div className="flex items-center justify-between pt-4 border-t border-slate-100">
                <p className="text-xs text-slate-400">
                  Added {new Date(place.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                </p>
                
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => { setSelectedPlace(place); setShowModal(true); }}
                    className="p-2 rounded-lg text-slate-400 hover:text-peacock-600 hover:bg-peacock-50 transition-colors"
                    title="View Details"
                  >
                    <FaEye className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => navigate(`/places/edit/${place.id}`)}
                    className="p-2 rounded-lg text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors"
                    title="Edit Place"
                  >
                    <FaEdit className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => { setPlaceToDelete(place); setShowDeleteModal(true); }}
                    className="p-2 rounded-lg text-slate-400 hover:text-ruby-600 hover:bg-ruby-50 transition-colors"
                    title="Delete Place"
                  >
                    <FaTrash className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Empty State */}
      {filteredPlaces.length === 0 && (
        <div className="card p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-slate-100 flex items-center justify-center">
            <FaMapMarkerAlt className="w-7 h-7 text-slate-400" />
          </div>
          <p className="text-slate-900 font-medium mb-1">No places found</p>
          <p className="text-slate-500 text-sm">Try adjusting your search or filter criteria</p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            className="p-2 rounded-xl border border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FaChevronLeft className="w-4 h-4" />
          </button>
          
          {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              onClick={() => setCurrentPage(page)}
              className={`w-10 h-10 rounded-xl font-medium transition-all ${
                currentPage === page
                  ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/25'
                  : 'border border-slate-200 text-slate-600 hover:border-slate-300'
              }`}
            >
              {page}
            </button>
          ))}
          
          <button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            className="p-2 rounded-xl border border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <FaChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Preview Modal */}
      {showModal && selectedPlace && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 z-10 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
              <h2 className="font-display text-xl font-semibold text-slate-900">{selectedPlace.name}</h2>
              <button 
                onClick={() => setShowModal(false)}
                className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors"
              >
                <FaTimes className="w-5 h-5" />
              </button>
            </div>
            
            <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-6 space-y-6">
              {/* Images */}
              {selectedPlace.images && selectedPlace.images.length > 0 && (
                <div className="grid grid-cols-2 gap-4">
                  {selectedPlace.images.map((image, index) => (
                    <div key={index} className="aspect-video rounded-xl overflow-hidden bg-slate-100">
                      <img
                        src={image}
                        alt={`${selectedPlace.name} ${index + 1}`}
                        className="w-full h-full object-cover"
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Info Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="card p-5">
                  <h3 className="font-semibold text-slate-900 mb-2">Description</h3>
                  <p className="text-slate-600 text-sm leading-relaxed">{selectedPlace.description}</p>
                </div>
                <div className="card p-5">
                  <h3 className="font-semibold text-slate-900 mb-2">Location</h3>
                  <p className="text-slate-600 text-sm">{selectedPlace.address}</p>
                  <p className="text-slate-500 text-sm mt-1">{selectedPlace.city}, {selectedPlace.state}</p>
                </div>
              </div>

              {/* Key Information */}
              {selectedPlace.key_information && selectedPlace.key_information.length > 0 && (
                <div className="card p-5">
                  <h3 className="font-semibold text-slate-900 mb-4">Key Information</h3>
                  <div className="space-y-3">
                    {selectedPlace.key_information.map((info, index) => (
                      <div key={index} className="bg-slate-50 rounded-xl p-4">
                        <h4 className="font-medium text-slate-900 mb-1">{info.question}</h4>
                        <p className="text-slate-600 text-sm">{info.answer}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="sticky bottom-0 bg-slate-50 border-t border-slate-100 px-6 py-4 flex justify-end gap-3">
              <button onClick={() => setShowModal(false)} className="btn btn-secondary">
                Close
              </button>
              <button
                onClick={() => { setShowModal(false); navigate(`/places/edit/${selectedPlace.id}`); }}
                className="btn btn-primary"
              >
                <FaEdit className="w-4 h-4 mr-2" />
                Edit Place
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && placeToDelete && (
        <div className="modal-overlay" onClick={() => { setShowDeleteModal(false); setPlaceToDelete(null); }}>
          <div 
            className="bg-white rounded-3xl shadow-2xl max-w-md w-full p-6 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="text-center mb-6">
              <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-ruby-100 flex items-center justify-center">
                <FaTrash className="w-7 h-7 text-ruby-500" />
              </div>
              <h2 className="font-display text-xl font-bold text-slate-900 mb-2">Delete Place?</h2>
              <p className="text-slate-500">
                Are you sure you want to delete <span className="font-semibold text-slate-900">{placeToDelete.name}</span>? This action cannot be undone.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => { setShowDeleteModal(false); setPlaceToDelete(null); }}
                className="btn btn-secondary flex-1"
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                className="btn btn-danger flex-1"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ViewData;
